import { BrowserRouter, HashRouter, Navigate, useRoutes } from 'react-router-dom'
import type { RouteObject } from 'react-router-dom'
import { isInTauriShell, isDigitalMode } from '@renderer/lib/runtime'
import { TauriAndroidBackBridge } from '../components/TauriAndroidBackBridge'
import { KioskGestureGuard } from '../components/KioskGestureGuard'
import Login from '../pages/Login'
import Register from '../pages/Register'
import Projects from '../pages/Projects'
import ProtectedRoute from './ProtectedRoute'
import ProjectRoute from './ProjectRoute'
import HubRoute from './HubRoute'
import NotFound from '../pages/NotFound'
import Print from '../pages/Print'
import UploadPage from '@renderer/pages/upload/UploadPage'
import StripGenerator from '@renderer/pages/strip/StripGenerator'
import Onboarding from '../pages/new/Onboarding'
import Birthday from '../pages/birthday/Birthday'
import SessionRedirect from '../pages/SessionRedirect'
// PostHog disabled
// import { PostHogPageView } from '../components/PostHogPageView'

/**
 * Build the route tree according to the current app mode.
 *
 * Kiosk mode (Electron, Tauri, app.dreamboothstudio.com, localhost):
 *   - All existing routes intact: /login, /register, /print, /hub/:hubId/*,
 *     /:projectId/*, /upload, /strip, / (Projects list), and a NotFound
 *     catch-all.
 *
 * Digital mode (dreambooth.app and subdomains):
 *   - /new → consumer onboarding wizard (stub in Step 1).
 *   - /g/:sessionId → redirect to studio public gallery.
 *   - /:projectId/* → kept; the param is interpreted as a slug by
 *     ProjectRoute when isDigitalMode() is true.
 *   - / → redirects to /new.
 *   - Operator-only pages (Projects list, /upload, /strip, /print) are
 *     not registered — visiting them resolves to NotFound.
 *
 * Keep this function pure of side-effects; it runs at render time.
 */
function buildRoutes(digital: boolean): RouteObject[] {
  if (digital) {
    // Note: `/login` and `/register` are NOT registered in digital mode.
    // Consumers don't have operator-style login in v1; future creator
    // accounts (Phase C) will use magic-link flow on the studio domain.
    // `/` redirects to `/new` from outside ProtectedRoute, so we never
    // trigger auth resolution for the empty path.
    return [
      { path: '/new', element: <Onboarding /> },
      // Micro apps: hardcoded, opinionated skins over the /new machinery.
      { path: '/birthday', element: <Birthday /> },
      { path: '/g/:sessionId', element: <SessionRedirect /> },
      { path: '/', element: <Navigate to="/new" replace /> },
      {
        element: <ProtectedRoute />,
        children: [{ path: '/:projectId/*', element: <ProjectRoute /> }]
      },
      { path: '*', element: <NotFound /> }
    ]
  }

  // Kiosk (existing).
  return [
    { path: '/login', element: <Login /> },
    { path: '/register', element: <Register /> },
    { path: '/print', element: <Print /> },
    // Site responsibility split: creation lives on dreambooth.app (/new),
    // operation lives on app.dreamboothstudio.com. A kiosk-host /new visit
    // bounces to the Projects list — WITHOUT the auto-start jump to the
    // default project (state flag read by Projects.tsx). Use ?mode=digital
    // to intentionally test the onboarding flow on the kiosk host.
    {
      path: '/new',
      element: <Navigate to="/" replace state={{ suppressAutoStart: true }} />
    },
    {
      path: '/birthday',
      element: <Navigate to="/" replace state={{ suppressAutoStart: true }} />
    },
    {
      element: <ProtectedRoute />,
      children: [
        // Before the `/:projectId/*` catch-all: that pattern matches `/hub/x`
        // too and would send the booth to load a project with the id "hub".
        // Kiosk only — a hub is an operator-facing entry point, and the public
        // web booth has no member list to pick from.
        { path: '/hub/:hubId/*', element: <HubRoute /> },
        { path: '/:projectId/*', element: <ProjectRoute /> },
        { path: '/upload', element: <UploadPage /> },
        { path: '/strip', element: <StripGenerator /> },
        { path: '/', element: <Projects /> },
        { path: '*', element: <NotFound /> }
      ]
    }
  ]
}

function Routes(): JSX.Element {
  // Mode is a pure function of shell + URL (no async flag), so the route
  // tree can be built synchronously. Pilot access is enforced server-side
  // on the project owner's flag — see lib/runtime/mode.ts.
  const digital = isDigitalMode()
  return useRoutes(buildRoutes(digital)) as React.ReactElement
}

export function AppRoutes(): JSX.Element {
  const isElectron = window.navigator.userAgent.toLowerCase().includes('electron')
  // Tauri WebView also needs hash routing (uses tauri://localhost scheme on Android,
  // and file:// on desktop — both incompatible with BrowserRouter's pushState routing).
  const Router = isElectron || isInTauriShell() ? HashRouter : BrowserRouter

  return (
    <Router>
      <TauriAndroidBackBridge />
      <KioskGestureGuard />
      {/* PostHog disabled */}
      {/* <PostHogPageView /> */}
      <Routes />
    </Router>
  )
}
